/* eslint-disable radix */
import React, { useState, useEffect, useContext } from 'react'
import Grid from '@material-ui/core/Grid'
import {
  FormControl,
  Typography,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@material-ui/core'
import { MultiSelect } from 'react-multi-select-component'
import AddIcon from '@material-ui/icons/AddCircleOutlineOutlined'
import { DateTimePicker } from '@material-ui/pickers'
import CalendarTodayIcon from '@material-ui/icons/CalendarTodayOutlined'

import moment, { Moment } from 'config/moment'
import { MULTI_SELECT_STRINGS } from 'consts/strings'
import { Dialog, UploadButton, snackbar } from 'components'
import { STRINGS, ERRORS } from 'consts'
import { useCheckFormErrors, useApiCall } from 'hooks'
import { UserContext } from 'contexts'
import { getUserRole, Role } from 'utils/roles'
import { CustomFormControlLabel } from 'components/CustomFormControlLabel'
import { CustomCheckbox } from 'components/CustomCheckbox'
import { PrizeCategory } from 'consts/prize'
import VisitsFilter from 'features/Main/Notifications/components/filters/VisitsFilter'
import DateRangeFilter from 'features/Main/Notifications/components/filters/DateRangeFilter'
import RewardFilter from 'features/Main/Notifications/components/filters/RewardFilter'
import { TabPanel } from 'components/Dialog/TabPanel'
import CustomTextField from 'components/CustomTextField'
import { ConfigUtils } from 'utils/config'
import {
  TimeRestrictionConfig,
  TimeRestrictionConfig as TimeRestrictionConfigType,
} from 'components/TimeRestrictionConfig'
import { resizeFile } from 'utils'

import { REWARD_TYPE_BY_PRIZES } from '../../../Memberships/Prizes/components/constants'
import { PartnerIntegration } from '../../../components/PartnerIntegration'
import { useStyles } from '../../../ManualRewards/components/CreateManualRewardDialog/styles'
import { campaignDialogStyle, campaignDialogContentStyle } from '../campaignDialogStyle'
import {
  getCampaignDialogTabs,
  getCampaignPopUpTabIndex,
  getCampaignEmailTabIndex,
  isCampaignPopUpsEnabled,
  isCampaignEmailEnabled,
  shouldShowPushImageUpload,
} from '../campaignFeatureFlags'
import { createCampaign, fetchPushUserCount, uploadPopUpImage } from '../../api'
import { createCampaignEmail } from '../../campaignEmailsApi'
import { Tag } from '../../../Tags/types'
import {
  Campaign,
  CreateCampaignPayload,
  CreateCampaignResponse,
  DataParams,
  DataResponse,
  UploadPopUpImageResponse,
} from '../../types'
import {
  CAMPAIGN_EMAIL_HTML_MAX_LENGTH,
  CAMPAIGN_EMAIL_SUBJECT_MAX_LENGTH,
  CreateCampaignEmailPayload,
  CreateCampaignEmailResponse,
} from '../../campaignEmailsTypes'
import { createPrize } from '../../../Prizes/api'
import { createNotification } from '../../../Notifications/api'
import { CreatePrizePayload, CreatePrizeResponse } from '../../../Prizes/types'
import { CreateNotificationPayload, CreateNotificationResponse } from '../../../Notifications/types'

interface ValidationData {
  toteatEnabled: boolean
  tucanCategory?: PrizeCategory
  toteatCategory?: PrizeCategory
  activeReward?: boolean
  activePush?: boolean
  activePopUp?: boolean
  activeEmail?: boolean
  isCampaignPopUpsEnabled?: boolean
  isCampaignEmailEnabled?: boolean
  hasPrizeImagePreview?: boolean
}

interface TagOption {
  label: string
  value: number
}

const rules = {
  title: [
    {
      validate: (title: string) => {
        if (!title || title.trim().length === 0) {
          return false
        }
        return true
      },
      message: 'El título es requerido',
      validateEmpty: true,
    },
    {
      validate: (title: string) => !title || title.length <= 100,
      message: 'Máximo 100 caracteres',
    },
  ],
  description: [
    {
      validate: (description: string) => {
        if (!description || description.trim().length === 0) {
          return false
        }
        return true
      },
      message: 'La descripción es requerida',
      validateEmpty: true,
    },
    {
      validate: (description: string) => !description || description.length <= 255,
      message: 'Máximo 255 caracteres',
    },
  ],
  startDate: [
    {
      validate: (value: Moment | null) => !!value,
      message: 'La fecha de inicio es requerida',
      validateEmpty: true,
    },
  ],
  prizeTitle: [
    {
      validate: (title: string, data: ValidationData) => {
        const { activeReward } = data
        if (activeReward && (!title || title.trim().length === 0)) {
          return false
        }
        return true
      },
      message: 'El título es requerido',
      validateEmpty: true,
    },
    {
      validate: (title: string) => !title || title.length <= 70,
      message: 'Máximo 70 caracteres',
    },
  ],
  prizeShortTitle: [
    {
      validate: (shortTitle: string, data: ValidationData) => {
        const { activeReward } = data
        if (activeReward && (!shortTitle || shortTitle.trim().length === 0)) {
          return false
        }
        return true
      },
      message: 'El título secundario es requerido',
      validateEmpty: true,
    },
    {
      validate: (shortTitle: string) => !shortTitle || shortTitle.length <= 100,
      message: 'Máximo 100 caracteres',
    },
  ],
  pushTitle: [
    {
      validate: (title?: string, data?: ValidationData) => {
        const { activePush } = data || {}
        if (activePush && (!title || title.trim().length === 0)) {
          return false
        }
        return true
      },
      message: 'El título push es requerido',
      validateEmpty: true,
    },
    {
      validate: (title?: string) => (title ? title.length <= 30 : true),
      message: 'Máximo 30 caracteres',
    },
  ],
  pushDescription: [
    {
      validate: (description?: string, data?: ValidationData) => {
        const { activePush } = data || {}
        if (activePush && (!description || description.trim().length === 0)) {
          return false
        }
        return true
      },
      message: 'La descripción push es requerida',
      validateEmpty: true,
    },
    {
      validate: (description?: string) => (description ? description.length <= 100 : true),
      message: 'Máximo 100 caracteres',
    },
  ],
  toteatBenefitId: [
    {
      validate: (value: string, data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.Checkout) {
          return value.trim().length > 0
        }
        return true
      },
      message: 'El ID de beneficio POS es requerido',
      validateEmpty: true,
    },
  ],
  skusList: [
    {
      validate: (skusList: string[], data: ValidationData) => {
        const { toteatEnabled, toteatCategory } = data
        if (toteatEnabled && toteatCategory === PrizeCategory.General) {
          return skusList.length > 0
        }
        return true
      },
      message: 'El SKU es requerido',
    },
  ],
  visitsFilter: [
    {
      validate: (visitsFilter: string | null) =>
        visitsFilter == null ? true : parseInt(visitsFilter) <= 1000,
      message: 'Máximo permitido: 1000',
    },
  ],
  prizeIcon: [
    {
      validate: (file: File | null, data: ValidationData) => {
        const { activeReward } = data
        if (activeReward) {
          return !!file
        }
        return true
      },
      message: 'El icono del reward es requerido',
      validateEmpty: true,
    },
  ],
  prizeImage: [
    {
      validate: (file: File | null, data: ValidationData) => {
        const { activeReward, activePush, isCampaignPopUpsEnabled: popUpsEnabled } = data
        if (activeReward && activePush && !popUpsEnabled) {
          return !!file
        }
        return true
      },
      message: 'La imagen de la push es requerida',
      validateEmpty: true,
    },
  ],
  popUpImageId: [
    {
      validate: (value: string | undefined, data: ValidationData) => {
        const { activePopUp, isCampaignPopUpsEnabled: popUpsEnabled } = data
        if (popUpsEnabled && activePopUp && (!value || value.trim().length === 0)) {
          return false
        }
        return true
      },
      message: 'La imagen del pop-up es requerida',
      validateEmpty: true,
    },
  ],
  emailSubject: [
    {
      validate: (subject?: string, data?: ValidationData) => {
        const { activeEmail, isCampaignEmailEnabled: emailEnabled } = data || {}
        if (emailEnabled && activeEmail && (!subject || subject.trim().length === 0)) {
          return false
        }
        return true
      },
      message: 'El asunto del email es requerido',
      validateEmpty: true,
    },
    {
      validate: (subject?: string) =>
        !subject || subject.length <= CAMPAIGN_EMAIL_SUBJECT_MAX_LENGTH,
      message: `Máximo ${CAMPAIGN_EMAIL_SUBJECT_MAX_LENGTH} caracteres`,
    },
  ],
  emailHtml: [
    {
      validate: (html?: string, data?: ValidationData) => {
        const { activeEmail, isCampaignEmailEnabled: emailEnabled } = data || {}
        if (emailEnabled && activeEmail && (!html || html.trim().length === 0)) {
          return false
        }
        return true
      },
      message: 'El HTML del email es requerido',
      validateEmpty: true,
    },
    {
      validate: (html?: string) => !html || html.length <= CAMPAIGN_EMAIL_HTML_MAX_LENGTH,
      message: `Máximo ${CAMPAIGN_EMAIL_HTML_MAX_LENGTH} caracteres`,
    },
  ],
}

interface CreateCampaignDialogProps {
  handleClose: () => void
  onDone: (newCampaign: Campaign) => void
  tags: Tag[]
}

const CreateCampaignDialog: React.FC<CreateCampaignDialogProps> = ({
  handleClose,
  onDone,
  tags,
}) => {
  const classes = useStyles()
  const { admin } = useContext(UserContext)
  const userRole = getUserRole(admin)
  const isMarketing = userRole === Role.Marketing

  // Campaign fields
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [startDate, setStartDate] = useState<Moment | null>(moment().add(1, 'hour'))
  const [endDate, setEndDate] = useState<Moment | null>(null)
  const [enabled, setEnabled] = useState(!isMarketing)
  const [activeReward, setActiveReward] = useState(false)
  const [activePush, setActivePush] = useState(false)
  const [activePopUp, setActivePopUp] = useState(false)
  const [activeEmail, setActiveEmail] = useState(false)

  // Segmentation fields
  const [tagsSelected, setTagsSelected] = useState<TagOption[]>([])
  const [excludedTagsSelected, setExcludedTagsSelected] = useState<TagOption[]>([])
  const [allTagsAreRequired, setAllTagsAreRequired] = useState(false)
  const [includeAbandoners, setIncludeAbandoners] = useState(false)
  const [visitsFilter, setVisitsFilter] = useState<string | null>(null)
  const [visitsOperator, setVisitsOperator] = useState<'==' | '>' | '<'>('==')
  const [dateFrom, setDateFrom] = useState<Moment | null>(null)
  const [dateTo, setDateTo] = useState<Moment | null>(null)
  const [rewardId, setRewardId] = useState<string>('')
  const [invertedLogic, setInvertedLogic] = useState<boolean>(false)

  // Prize fields
  const [prizeTitle, setPrizeTitle] = useState('')
  const [prizeShortTitle, setPrizeShortTitle] = useState('')
  const [prizeIcon, setPrizeIcon] = useState<File | null>(null)
  const [prizeIconPreview, setPrizeIconPreview] = useState('')
  const [prizeImage, setPrizeImage] = useState<File | null>(null)
  const [prizeImagePreview, setPrizeImagePreview] = useState('')
  const [rewardType, setRewardType] = useState('M')
  const [toteatBenefitId, setToteatBenefitId] = useState<string>('')
  const [currentSku, setCurrentSku] = useState<string>('')
  const [skusList, setSkusList] = useState<string[]>([])
  const [storesEnabled, setStoresEnabled] = useState(true)
  const [tucanEnabled, setTucanEnabled] = useState(false)
  const [toteatEnabled, setToteatEnabled] = useState(false)
  const [toteatCategory, setToteatCategory] = useState<PrizeCategory | undefined>(
    PrizeCategory.Checkout,
  )
  const [tucanCategory, setTucanCategory] = useState<PrizeCategory | undefined>(
    PrizeCategory.Checkout,
  )
  const [timeRestriction, setTimeRestriction] = useState<TimeRestrictionConfigType>({
    timeRestrictionEnabled: false,
    allowedDays: [],
    startTime: '00:00',
    endTime: '23:59',
  })

  // Push fields
  const [pushTitle, setPushTitle] = useState('')
  const [pushDescription, setPushDescription] = useState('')

  // Pop-up fields
  const [popUpImagePreview, setPopUpImagePreview] = useState('')
  const [popUpImageId, setPopUpImageId] = useState('')
  const [isUploadingPopUpImage, setIsUploadingPopUpImage] = useState(false)

  // Email fields
  const [emailSubject, setEmailSubject] = useState('')
  const [emailHtml, setEmailHtml] = useState('')

  const [userCount, setUserCount] = useState<number | null>(null)
  const [hasPartnerErrors, setHasPartnerErrors] = useState(false)
  const [tabIndex, setTabIndex] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)

  // Ajustar tabIndex si se desactiva el reward y estamos en el tab de Horarios
  /* useEffect(() => {
    if (!activeReward && tabIndex === 3) {
      // Si se desactiva el reward y estamos en Horarios, volver al tab de Beneficio
      setTabIndex(2)
    }
  }, [activeReward, tabIndex]) */

  const tagsForSelect = tags.map(el => ({ label: el.title, value: el.id }))
  const includeTagOptions = tagsForSelect.filter(
    tag => !excludedTagsSelected.some(selected => selected.value === tag.value),
  )
  const excludeTagOptions = tagsForSelect.filter(
    tag => !tagsSelected.some(selected => selected.value === tag.value),
  )

  const [createCampaignApi, isLoading] = useApiCall<CreateCampaignPayload, CreateCampaignResponse>(
    createCampaign,
  )
  const [createPrizeApi] = useApiCall<CreatePrizePayload, CreatePrizeResponse>(createPrize)
  const [createNotificationApi] = useApiCall<CreateNotificationPayload, CreateNotificationResponse>(
    createNotification,
  )
  const [createCampaignEmailApi] = useApiCall<
    CreateCampaignEmailPayload,
    CreateCampaignEmailResponse
  >(createCampaignEmail)
  const [uploadPopUpImageApi] = useApiCall<File, UploadPopUpImageResponse>(uploadPopUpImage)
  const [requestData, isRequestingData] = useApiCall<DataParams, DataResponse>(fetchPushUserCount)

  const handleSetTags = async (e: TagOption[]) => {
    const excludedIds = new Set(excludedTagsSelected.map(tag => tag.value))
    setTagsSelected(e.filter(tag => !excludedIds.has(tag.value)))
  }

  const handleSetExcludedTags = async (e: TagOption[]) => {
    const includedIds = new Set(tagsSelected.map(tag => tag.value))
    setExcludedTagsSelected(e.filter(tag => !includedIds.has(tag.value)))
  }

  const handleAllTagsAreRequiredChange: React.ChangeEventHandler<HTMLInputElement> = () => {
    setAllTagsAreRequired(!allTagsAreRequired)
  }

  const handleStartDateChange = (date: Moment | null) => {
    setStartDate(date)
  }

  const handleEndDateChange = (date: Moment | null) => {
    setEndDate(date)
  }

  const handlePrizeIconChange = async (file: File) => {
    setPrizeIconPreview(URL.createObjectURL(file))
    const newIcon = await resizeFile(file, 192, 192)
    setPrizeIcon(newIcon)
  }

  const handlePrizeImageChange = async (file: File) => {
    setPrizeImagePreview(URL.createObjectURL(file))
    const newImage = await resizeFile(file, 960, 640)
    setPrizeImage(newImage)
  }

  const handlePopUpImageChange = async (file: File) => {
    const localPreviewUrl = URL.createObjectURL(file)
    setPopUpImagePreview(localPreviewUrl)
    setIsUploadingPopUpImage(true)
    try {
      const { popUpImageId: uploadedId, popUpImageUrl } = await uploadPopUpImageApi(file)
      setPopUpImageId(uploadedId)
      if (popUpImageUrl) {
        URL.revokeObjectURL(localPreviewUrl)
        setPopUpImagePreview(popUpImageUrl)
      }
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo subir la imagen del pop-up. Intente de nuevo.')
      URL.revokeObjectURL(localPreviewUrl)
      setPopUpImagePreview('')
      setPopUpImageId('')
    } finally {
      setIsUploadingPopUpImage(false)
    }
  }

  const handleRewardTypeSelect = (event: React.ChangeEvent<{ value: unknown }>) => {
    setRewardType(event.target.value as string)
  }

  const handleFetchPushUserCount = async () => {
    try {
      const tagsSelectedId = tagsSelected.map((tag: { value: number }) => tag.value)
      const excludedTagsSelectedId = excludedTagsSelected.map((tag: { value: number }) => tag.value)

      const filterDates =
        dateFrom && dateTo
          ? {
              dateFrom: dateFrom?.toISOString(),
              dateTo: dateTo?.toISOString(),
            }
          : undefined

      const data = await requestData({
        visitsValue:
          visitsFilter !== null && visitsFilter.length ? parseInt(visitsFilter) : undefined,
        visitsOperator,
        ...filterDates,
        rewardId: rewardId ? parseInt(rewardId) : undefined,
        rewardInverted: invertedLogic,
        tagIds: tagsSelectedId,
        excludedTagIds: excludedTagsSelectedId,
        allTagsAreRequired,
        includeAbandoners,
      })

      setUserCount(data.count)
    } catch (err) {
      console.error(err)
      snackbar.show('No se pudo calcular la cantidad de usuarios. Intente de nuevo.')
    }
  }

  useEffect(() => {
    handleFetchPushUserCount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rewardId,
    invertedLogic,
    tagsSelected,
    excludedTagsSelected,
    allTagsAreRequired,
    includeAbandoners,
    visitsFilter,
    visitsOperator,
    dateFrom,
    dateTo,
  ])

  const getRequiredFields = () => {
    const requiredFields: string[] = ['startDate', 'title', 'description']

    if (activeReward) {
      requiredFields.push('prizeTitle', 'prizeShortTitle', 'prizeIcon')
    }

    if (activePush) {
      requiredFields.push('pushTitle', 'pushDescription')
      if (activeReward && shouldShowPushImageUpload()) {
        requiredFields.push('prizeImage')
      }
    }

    if (isCampaignPopUpsEnabled() && activePopUp) {
      requiredFields.push('popUpImageId')
    }

    if (isCampaignEmailEnabled() && activeEmail) {
      requiredFields.push('emailSubject', 'emailHtml')
    }

    if (toteatEnabled) {
      if (toteatCategory === PrizeCategory.General) {
        requiredFields.push('skusList')
      } else if (toteatCategory === PrizeCategory.Checkout) {
        requiredFields.push('toteatBenefitId')
      }
    }

    return requiredFields
  }

  const fields = {
    title,
    description,
    startDate,
    prizeTitle,
    prizeShortTitle,
    prizeIcon,
    prizeImage,
    pushTitle,
    pushDescription,
    emailSubject,
    emailHtml,
    toteatEnabled,
    toteatBenefitId,
    skusList,
    tucanCategory,
    toteatCategory,
    visitsFilter,
    activeReward,
    activePush,
    activePopUp: isCampaignPopUpsEnabled() ? activePopUp : false,
    activeEmail: isCampaignEmailEnabled() ? activeEmail : false,
    popUpImageId,
    isCampaignPopUpsEnabled: isCampaignPopUpsEnabled(),
    isCampaignEmailEnabled: isCampaignEmailEnabled(),
  }
  const { hasErrors, isAnyFieldEmpty, errors } = useCheckFormErrors(
    fields,
    rules,
    getRequiredFields(),
  )
  // Only disable button during loading/submitting, not based on validation
  const isSubmitDisabled = isLoading || isSubmitting

  const onCreateCampaign = async () => {
    if (isSubmitting) return

    // Mark that user has attempted to submit
    setHasAttemptedSubmit(true)

    // Check for validation errors
    if (hasErrors || isAnyFieldEmpty || hasPartnerErrors) {
      // Use setTimeout to ensure snackbar renders before return
      setTimeout(() => {
        snackbar.show('Error al crear campaña. Verifique los campos obligatorios .')
      }, 0)
      return
    }

    // Validate that at least one channel is active
    if (
      !activeReward &&
      !activePush &&
      !(isCampaignPopUpsEnabled() && activePopUp) &&
      !(isCampaignEmailEnabled() && activeEmail)
    ) {
      setTimeout(() => {
        snackbar.show(
          'Error al crear campaña. Debe activar al menos un beneficio, una push, un pop-up o un email.',
        )
      }, 0)
      return
    }

    if (!startDate) {
      // Use setTimeout to ensure snackbar renders before return
      setTimeout(() => {
        snackbar.show('Error al crear campaña. Verifique el inicio de campaña.')
      }, 0)
      return
    }

    let prizeId: number | undefined
    let notificationId: number | undefined
    let emailId: number | undefined

    setIsSubmitting(true)
    try {
      // Create prize if activeReward is true
      if (activeReward) {
        if (!prizeIcon || !prizeTitle || !prizeShortTitle) {
          snackbar.show('Todos los campos del premio son requeridos.')
          return
        }

        /* // Use endDate if available, otherwise use startDate
        const expirationDate = endDate || startDate
        if (!expirationDate) {
          snackbar.show('La fecha de fin de la campaña es requerida para el premio.')
          return
        } */

        // Format timeRestriction to ensure HH:MM format (remove seconds if present)
        const formattedTimeRestriction = timeRestriction
          ? {
              ...timeRestriction,
              startTime: timeRestriction.startTime
                ? timeRestriction.startTime.substring(0, 5)
                : '00:00',
              endTime: timeRestriction.endTime ? timeRestriction.endTime.substring(0, 5) : '23:59',
            }
          : undefined

        const prizePayload: CreatePrizePayload = {
          title: prizeTitle,
          additionalText: prizeShortTitle,
          icon: prizeIcon,
          image: prizeImage || undefined,
          rewardType: rewardType || 'M',
          active: true,
          storesEnabled,
          tucanEnabled,
          toteatEnabled,
          tucanCategory,
          toteatCategory,
          toteatBenefitId: toteatBenefitId || undefined,
          skus: skusList,
          tagsSelected: tagsSelected.map(t => ({ label: t.label, value: t.value })),
          allTagsAreRequired,
          startDate: startDate.toISOString(),
          expiration: endDate ? endDate.toISOString() : undefined,
          timeRestriction: formattedTimeRestriction,
        }

        const { prize } = await createPrizeApi(prizePayload)
        prizeId = prize.id
      }

      // Create notification if activePush is true
      if (activePush) {
        if (!pushTitle || !pushDescription) {
          snackbar.show('Todos los campos de la push son requeridos.')
          return
        }

        const tagsSelectedId = tagsSelected.map((tag: { value: number }) => tag.value)
        const notificationPayload: CreateNotificationPayload = {
          title: pushTitle,
          body: pushDescription,
          scheduled: startDate.toISOString(),
          tagIds: tagsSelectedId,
          allTagsAreRequired,
          visitsOperator: visitsFilter !== null ? visitsOperator : undefined,
          visitsValue: visitsFilter !== null ? parseInt(visitsFilter) : undefined,
          dateFrom: dateFrom?.toISOString(),
          dateTo: dateTo?.toISOString(),
          rewardId: rewardId ? parseInt(rewardId) : undefined,
          rewardInverted: invertedLogic,
        }

        const { notification } = await createNotificationApi(notificationPayload)
        notificationId = notification.id
      }

      if (isCampaignEmailEnabled() && activeEmail) {
        if (!emailSubject.trim() || !emailHtml.trim()) {
          snackbar.show('Todos los campos del email son requeridos.')
          return
        }

        const { campaignEmail } = await createCampaignEmailApi({
          subject: emailSubject.trim(),
          html: emailHtml,
        })
        emailId = campaignEmail.id
      }

      // Create campaign
      const tagsSelectedId = tagsSelected.map((tag: { value: number }) => tag.value)
      const campaignPayload: CreateCampaignPayload = {
        title: title || undefined,
        description: description || undefined,
        startDate: startDate.toISOString(),
        endDate: endDate?.toISOString() || undefined,
        notificationId,
        prizeId,
        emailId,
        enabled,
        activeReward,
        activePush,
        activeEmail: isCampaignEmailEnabled() ? activeEmail : false,
        activePopUp: isCampaignPopUpsEnabled() ? activePopUp : false,
        popUpImageId: isCampaignPopUpsEnabled() && activePopUp ? popUpImageId : undefined,
        tagIds: tagsSelectedId,
        excludedTagIds: excludedTagsSelected.map((tag: { value: number }) => tag.value),
        allTagsAreRequired,
        visitsOperator: visitsFilter !== null ? visitsOperator : undefined,
        visitsValue: visitsFilter !== null ? parseInt(visitsFilter) : undefined,
        dateFrom: dateFrom?.toISOString(),
        dateTo: dateTo?.toISOString(),
        rewardId: rewardId ? parseInt(rewardId) : undefined,
        rewardInverted: invertedLogic,
        includeAbandoners,
      }

      const { campaign } = await createCampaignApi(campaignPayload)
      onDone(campaign)
    } catch (err) {
      console.error(err)
      const errorMessage = err instanceof Error ? err.message : ERRORS.GENERIC_ERROR_MESSAGE
      snackbar.show(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePartnerValidationChange = (hasErrorsPartner: boolean) => {
    setHasPartnerErrors(hasErrorsPartner)
  }

  const hasAnyIntegration = ConfigUtils.hasAnyIntegrationEnabled()
  const isEcardEnabled = process.env.REACT_APP_ECARD_ENABLED === 'true'
  const campaignPopUpsEnabled = isCampaignPopUpsEnabled()
  const campaignEmailEnabled = isCampaignEmailEnabled()
  const showPushImageUpload = shouldShowPushImageUpload()
  const tabs = getCampaignDialogTabs(activeReward)
  const popUpTabIndex = getCampaignPopUpTabIndex(activeReward)
  const emailTabIndex = getCampaignEmailTabIndex(activeReward)
  const hasNoActiveChannel =
    !activeReward &&
    !activePush &&
    !(campaignPopUpsEnabled && activePopUp) &&
    !(campaignEmailEnabled && activeEmail)

  useEffect(() => {
    const maxTabIndex = tabs.length - 1
    if (tabIndex > maxTabIndex) {
      setTabIndex(maxTabIndex)
    }
  }, [tabs.length, tabIndex])

  console.log('showPushImageUpload', showPushImageUpload)
  console.log('activeReward', activeReward)

  return (
    <Dialog
      title="Nueva campaña"
      isOpen
      maxWidth={false}
      showTabsActions
      onCancel={handleClose}
      isLoading={isLoading || isSubmitting || isUploadingPopUpImage}
      okButtonText="Crear"
      okButtonProps={{ disabled: isSubmitDisabled, startIcon: <AddIcon /> }}
      onAccept={onCreateCampaign}
      tabs={tabs}
      tabIndex={tabIndex}
      setTabIndex={setTabIndex}
      showUserCount
      isRequestingData={isRequestingData}
      userCount={userCount!}
      userCountExtra={
        <div style={{ padding: '0px 24px 15px' }}>
          <CustomFormControlLabel
            control={
              <CustomCheckbox
                onChange={() => setIncludeAbandoners(!includeAbandoners)}
                checked={includeAbandoners}
              />
            }
            label="Incluir también abandonadores"
          />
        </div>
      }
      style={campaignDialogStyle}
      contentStyle={campaignDialogContentStyle}
    >
      <Grid>
        {/* Tab 0 Campaña */}
        <TabPanel value={tabIndex} index={0} id="campana">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <CustomTextField
                label="Título*"
                value={title}
                fullWidth
                onChange={e => setTitle(e.target.value)}
                disabled={isLoading}
                required
                maxLength={100}
                error={hasAttemptedSubmit && errors.title?.hasError}
                helperText={hasAttemptedSubmit ? errors.title?.message : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomTextField
                label="Descripción*"
                value={description}
                fullWidth
                onChange={e => setDescription(e.target.value)}
                disabled={isLoading}
                multiline
                rows={3}
                required
                maxLength={255}
                error={hasAttemptedSubmit && errors.description?.hasError}
                helperText={hasAttemptedSubmit ? errors.description?.message : ''}
              />
            </Grid>
            <Grid item xs={12}>
              <DateTimePicker
                value={startDate}
                onChange={handleStartDateChange}
                autoOk
                disablePast
                format={STRINGS.DATE_TIME_FORMAT}
                initialFocusedDate={new Date()}
                disabled={isLoading}
                label="Fecha de inicio*"
                required
                style={{ width: '100%' }}
                TextFieldComponent={props => (
                  <CustomTextField
                    {...props}
                    fullWidth
                    error={hasAttemptedSubmit && errors.startDate?.hasError}
                    helperText={hasAttemptedSubmit ? errors.startDate?.message : ''}
                    InputProps={{
                      ...props.InputProps,
                      endAdornment: (
                        <IconButton>
                          <CalendarTodayIcon style={{ width: 15, height: 15, color: '#6B7280' }} />
                        </IconButton>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <DateTimePicker
                value={endDate}
                onChange={handleEndDateChange}
                autoOk
                disablePast
                format={STRINGS.DATE_TIME_FORMAT}
                minDate={startDate || moment()}
                minDateMessage="La fecha de fin debe ser mayor o igual a la fecha de inicio"
                disabled={isLoading}
                label="Fecha de fin"
                style={{ width: '100%' }}
                TextFieldComponent={props => (
                  <CustomTextField
                    {...props}
                    fullWidth
                    InputProps={{
                      ...props.InputProps,
                      endAdornment: (
                        <IconButton>
                          <CalendarTodayIcon style={{ width: 15, height: 15, color: '#6B7280' }} />
                        </IconButton>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={e => setEnabled(e.target.checked)}
                    checked={enabled}
                    disabled={isLoading || isMarketing}
                  />
                }
                label="Habilitar campaña"
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 1 Segmentar */}
        <TabPanel value={tabIndex} index={1} id="segmentar">
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography className={classes.dialogSubTitle}>Tags a incluir</Typography>
              <FormControl className={classes.fullWidth}>
                <MultiSelect
                  options={includeTagOptions}
                  value={tagsSelected}
                  onChange={handleSetTags}
                  labelledBy="Seleccionar Tags"
                  hasSelectAll={false}
                  className={classes.outlinedMultiSelect}
                  overrideStrings={MULTI_SELECT_STRINGS}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={handleAllTagsAreRequiredChange}
                    checked={allTagsAreRequired}
                  />
                }
                label="Sólo usuarios con todos los tags seleccionados a incluir"
              />
            </Grid>
            <Grid item xs={12}>
              <Typography className={classes.dialogSubTitle}>Tags a excluir</Typography>
              <FormControl className={classes.fullWidth}>
                <MultiSelect
                  options={excludeTagOptions}
                  value={excludedTagsSelected}
                  onChange={handleSetExcludedTags}
                  labelledBy="Seleccionar Tags a excluir"
                  hasSelectAll={false}
                  className={classes.outlinedMultiSelect}
                  overrideStrings={MULTI_SELECT_STRINGS}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.dialogSubTitle}>Rango de visitantes</Typography>
              <Grid item xs={12}>
                <VisitsFilter
                  visitsFilter={visitsFilter}
                  setVisitsFilter={setVisitsFilter}
                  visitsOperator={visitsOperator}
                  setVisitsOperator={setVisitsOperator}
                  error={hasAttemptedSubmit && errors.visitsFilter?.hasError}
                  helperText={hasAttemptedSubmit ? errors.visitsFilter?.message : ''}
                />
              </Grid>
              <Grid item xs={12}>
                <DateRangeFilter
                  startDate={dateFrom}
                  setStartDate={setDateFrom}
                  endDate={dateTo}
                  setEndDate={setDateTo}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.dialogSubTitle}>Premios</Typography>
              <RewardFilter
                rewardId={rewardId}
                setRewardId={setRewardId}
                invertedLogic={invertedLogic}
                setInvertedLogic={setInvertedLogic}
              />
            </Grid>
          </Grid>
        </TabPanel>

        {/* Tab 2 Beneficio */}
        <TabPanel value={tabIndex} index={2} id="beneficio">
          <Grid container spacing={3}>
            <Grid item xs={12} className={classes.topCheckbox}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={e => setActiveReward(e.target.checked)}
                    checked={activeReward}
                  />
                }
                label="Activar Reward"
              />
              {hasAttemptedSubmit &&
                !activeReward &&
                !activePush &&
                !(campaignPopUpsEnabled && activePopUp) && (
                  <Typography
                    variant="caption"
                    color="error"
                    style={{ marginTop: 4, display: 'block', marginLeft: 40 }}
                  >
                    Debe activar al menos un beneficio, una push o un pop-up para crear la campaña.
                  </Typography>
                )}
            </Grid>

            {activeReward && (
              <>
                <Grid item xs={12}>
                  <FormControl className={classes.fullWidth}>
                    <InputLabel
                      id="reward-type-select-label"
                      style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14 }}
                    >
                      Tipo de premio
                    </InputLabel>
                    <Select
                      style={{ fontFamily: 'Montserrat', fontWeight: 400, fontSize: 14 }}
                      labelId="reward-type-select-label"
                      id="reward-type-select"
                      value={rewardType}
                      onChange={handleRewardTypeSelect}
                      fullWidth
                    >
                      {REWARD_TYPE_BY_PRIZES.map(value => (
                        <MenuItem value={value.id} key={value.id}>
                          {value.description}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Título*"
                    value={prizeTitle}
                    fullWidth
                    onChange={e => setPrizeTitle(e.target.value)}
                    disabled={isLoading}
                    required
                    maxLength={70}
                    error={hasAttemptedSubmit && errors.prizeTitle?.hasError}
                    helperText={
                      hasAttemptedSubmit
                        ? errors.prizeTitle?.message || rules.prizeTitle[0].message
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Título secundario*"
                    value={prizeShortTitle}
                    fullWidth
                    onChange={e => setPrizeShortTitle(e.target.value)}
                    disabled={isLoading}
                    required
                    maxLength={100}
                    error={hasAttemptedSubmit && errors.prizeShortTitle?.hasError}
                    helperText={
                      hasAttemptedSubmit
                        ? errors.prizeShortTitle?.message || rules.prizeShortTitle[0].message
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container className={classes.iconUploadContainer}>
                    <Grid item xs={6} className={classes.iconUploadButton}>
                      <UploadButton
                        id="campaign-prize-icon-upload-button"
                        accept=".jpg, .jpeg, .png"
                        label="Subir icono del reward*"
                        onChange={handlePrizeIconChange}
                        maxSizeInMB={2}
                        disabled={isLoading}
                      />
                      {hasAttemptedSubmit && errors.prizeIcon?.hasError && (
                        <Typography
                          variant="caption"
                          color="error"
                          style={{ marginTop: 4, display: 'block' }}
                        >
                          {errors.prizeIcon.message}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <div className={classes.iconUploadPreviewContainer}>
                        {prizeIconPreview && (
                          <img src={prizeIconPreview} alt="" className={classes.iconPreview} />
                        )}
                      </div>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Integración section */}
                {hasAnyIntegration && (
                  <>
                    <Grid item xs={12}>
                      <Typography className={classes.dialogSubTitle} style={{ marginTop: 20 }}>
                        Integración
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <PartnerIntegration
                        hideTitle
                        storesEnabled={storesEnabled}
                        setStoresEnabled={setStoresEnabled}
                        tucanEnabled={tucanEnabled}
                        setTucanEnabled={setTucanEnabled}
                        toteatEnabled={toteatEnabled}
                        setToteatEnabled={setToteatEnabled}
                        tucanCategory={tucanCategory}
                        setTucanCategory={setTucanCategory}
                        toteatCategory={toteatCategory}
                        setToteatCategory={setToteatCategory}
                        currentSku={currentSku}
                        setCurrentSku={setCurrentSku}
                        skusList={skusList}
                        setSkusList={setSkusList}
                        toteatBenefitId={toteatBenefitId}
                        setToteatBenefitId={setToteatBenefitId}
                        errors={errors}
                        isLoading={isLoading}
                        onValidationChange={handlePartnerValidationChange}
                        showErrors={hasAttemptedSubmit}
                      />
                    </Grid>
                  </>
                )}
              </>
            )}
          </Grid>
        </TabPanel>

        {/* Tab 3 Horarios - Solo se muestra si activeReward es true */}
        {activeReward && (
          <TabPanel value={tabIndex} index={3} id="time-restriction">
            <TimeRestrictionConfig
              config={timeRestriction}
              onChange={setTimeRestriction}
              disabled={isLoading}
              hideTimeSelectors={isEcardEnabled}
            />
          </TabPanel>
        )}

        {/* Tab 4 Push (o Tab 3 si no hay Horarios) */}
        <TabPanel value={tabIndex} index={activeReward ? 4 : 3} id="push">
          <Grid container spacing={3}>
            <Grid item xs={12} className={classes.topCheckbox}>
              <CustomFormControlLabel
                control={
                  <CustomCheckbox
                    onChange={e => setActivePush(e.target.checked)}
                    checked={activePush}
                  />
                }
                label="Sale con push?"
              />
              {hasAttemptedSubmit && hasNoActiveChannel && (
                <Typography
                  variant="caption"
                  color="error"
                  style={{ marginTop: 4, display: 'block', marginLeft: 40 }}
                >
                  Debe activar al menos un beneficio, una push, un pop-up o un email para crear la
                  campaña.
                </Typography>
              )}
            </Grid>

            {activePush && (
              <>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Título push*"
                    value={pushTitle}
                    fullWidth
                    required
                    maxLength={30}
                    error={hasAttemptedSubmit && errors.pushTitle?.hasError}
                    helperText={
                      hasAttemptedSubmit
                        ? errors.pushTitle?.message || rules.pushTitle[0].message
                        : ''
                    }
                    onChange={e => setPushTitle(e.target.value)}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12}>
                  <CustomTextField
                    label="Descripción push*"
                    value={pushDescription}
                    fullWidth
                    required
                    multiline
                    maxLength={100}
                    error={hasAttemptedSubmit && errors.pushDescription?.hasError}
                    helperText={
                      hasAttemptedSubmit
                        ? errors.pushDescription?.message || rules.pushDescription[0].message
                        : ''
                    }
                    onChange={e => setPushDescription(e.target.value)}
                    disabled={isLoading}
                  />
                </Grid>
                {showPushImageUpload && activeReward && (
                  <Grid item xs={12}>
                    <Grid container className={classes.iconUploadContainer}>
                      <Grid item xs={6} className={classes.iconUploadButton}>
                        <UploadButton
                          id="campaign-prize-image-upload-button"
                          accept=".jpg, .jpeg, .png"
                          label="Subir imagen de la push*"
                          onChange={handlePrizeImageChange}
                          maxSizeInMB={2}
                        />
                        {hasAttemptedSubmit && errors.prizeImage?.hasError && (
                          <Typography
                            variant="caption"
                            color="error"
                            style={{ marginTop: 4, display: 'block' }}
                          >
                            {errors.prizeImage.message}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <div className={classes.iconUploadPreviewContainer}>
                          {prizeImagePreview && (
                            <img src={prizeImagePreview} alt="" className={classes.iconPreview} />
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </>
            )}
          </Grid>
        </TabPanel>

        {campaignPopUpsEnabled && (
          <TabPanel value={tabIndex} index={popUpTabIndex} id="pop-up">
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.topCheckbox}>
                <CustomFormControlLabel
                  control={
                    <CustomCheckbox
                      onChange={e => setActivePopUp(e.target.checked)}
                      checked={activePopUp}
                    />
                  }
                  label="Activar Pop-up"
                />
                {hasAttemptedSubmit && hasNoActiveChannel && (
                  <Typography
                    variant="caption"
                    color="error"
                    style={{ marginTop: 4, display: 'block', marginLeft: 40 }}
                  >
                    Debe activar al menos un beneficio, una push, un pop-up o un email para crear la
                    campaña.
                  </Typography>
                )}
              </Grid>

              {activePopUp && (
                <>
                  <Grid item xs={12}>
                    <Grid container className={classes.iconUploadContainer}>
                      <Grid item xs={6} className={classes.iconUploadButton}>
                        <UploadButton
                          id="campaign-pop-up-image-upload-button"
                          accept=".jpg, .jpeg, .png, .webp"
                          label="Subir imagen del pop-up*"
                          onChange={handlePopUpImageChange}
                          maxSizeInMB={2}
                          disabled={isLoading || isUploadingPopUpImage}
                        />
                        <Typography variant="caption" display="block" style={{ marginTop: 4 }}>
                          Formato vertical recomendado 9:16. Máximo 2MB (PNG, JPG, WebP).
                        </Typography>
                        {hasAttemptedSubmit && errors.popUpImageId?.hasError && (
                          <Typography
                            variant="caption"
                            color="error"
                            style={{ marginTop: 4, display: 'block' }}
                          >
                            {errors.popUpImageId.message}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <div className={classes.iconUploadPreviewContainer}>
                          {popUpImagePreview && (
                            <img
                              src={popUpImagePreview}
                              alt=""
                              className={classes.iconPreview}
                              style={{ aspectRatio: '9 / 16', objectFit: 'cover' }}
                            />
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </Grid>
                </>
              )}
            </Grid>
          </TabPanel>
        )}

        {campaignEmailEnabled && (
          <TabPanel value={tabIndex} index={emailTabIndex} id="email">
            <Grid container spacing={3}>
              <Grid item xs={12} className={classes.topCheckbox}>
                <CustomFormControlLabel
                  control={
                    <CustomCheckbox
                      onChange={e => setActiveEmail(e.target.checked)}
                      checked={activeEmail}
                    />
                  }
                  label="Enviar email?"
                />
                {hasAttemptedSubmit && hasNoActiveChannel && (
                  <Typography
                    variant="caption"
                    color="error"
                    style={{ marginTop: 4, display: 'block', marginLeft: 40 }}
                  >
                    Debe activar al menos un beneficio, una push, un pop-up o un email para crear la
                    campaña.
                  </Typography>
                )}
              </Grid>

              {activeEmail && (
                <>
                  <Grid item xs={12}>
                    <CustomTextField
                      label="Asunto*"
                      value={emailSubject}
                      fullWidth
                      required
                      maxLength={CAMPAIGN_EMAIL_SUBJECT_MAX_LENGTH}
                      error={hasAttemptedSubmit && errors.emailSubject?.hasError}
                      helperText={
                        hasAttemptedSubmit
                          ? errors.emailSubject?.message || rules.emailSubject[0].message
                          : ''
                      }
                      onChange={e => setEmailSubject(e.target.value)}
                      disabled={isLoading}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <CustomTextField
                      label="HTML*"
                      value={emailHtml}
                      fullWidth
                      required
                      multiline
                      rows={10}
                      error={hasAttemptedSubmit && errors.emailHtml?.hasError}
                      helperText={
                        hasAttemptedSubmit
                          ? errors.emailHtml?.message || rules.emailHtml[0].message
                          : `${emailHtml.length} / ${CAMPAIGN_EMAIL_HTML_MAX_LENGTH}`
                      }
                      onChange={e => setEmailHtml(e.target.value)}
                      disabled={isLoading}
                      inputProps={{ maxLength: CAMPAIGN_EMAIL_HTML_MAX_LENGTH }}
                    />
                  </Grid>
                  {emailHtml.trim().length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                        Preview
                      </Typography>
                      <iframe
                        title="email-preview"
                        srcDoc={emailHtml}
                        sandbox=""
                        style={{
                          width: '100%',
                          minHeight: 360,
                          border: '1px solid #ddd',
                          borderRadius: 4,
                          background: '#fff',
                        }}
                      />
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          </TabPanel>
        )}
      </Grid>
    </Dialog>
  )
}

export { CreateCampaignDialog }
